// PWA utility functions

// Check if app is running as PWA
export const isPWA = () => {
  return window.matchMedia && window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

// Check if device supports PWA installation
export const canInstallPWA = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

// Get PWA display mode
export const getPWADisplayMode = () => {
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return 'standalone';
  }
  if (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches) {
    return 'fullscreen';
  }
  if (window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches) {
    return 'minimal-ui';
  }
  return 'browser';
};

// Check if app is installed
export const isAppInstalled = () => {
  return isPWA() || window.navigator.standalone === true;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

// Show local notification
export const showLocalNotification = (title, options = {}) => {
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      icon: '/logo192.png',
      badge: '/favicon.ico',
      ...options
    });

    // Auto close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    return notification;
  }
};

// Check if device is iOS
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Check if device is Android
export const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};

// Get device type for PWA optimization
export const getDeviceType = () => {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  return 'desktop';
};

// Handle PWA update available
export const handlePWAUpdate = (registration) => {
  if (registration && registration.waiting) {
    // New service worker is waiting, show update prompt
    const updateAvailable = window.confirm(
      'A new version of CalorieNSweat is available. Would you like to update now?'
    );
    
    if (updateAvailable) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }
};

// Clear all application caches
export const clearAllCaches = async () => {
  try {
    // Clear Cache Storage
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    console.log('All caches cleared');
    
    // Clear Local Storage
    localStorage.clear();
    console.log('Local storage cleared');
    
    // Clear Session Storage
    sessionStorage.clear();
    console.log('Session storage cleared');
    
    // Clear IndexedDB (if any)
    if ('indexedDB' in window) {
      // Note: This is a basic clear, might need more specific clearing based on your IndexedDB usage
      try {
        const databases = await indexedDB.databases();
        await Promise.all(
          databases.map(db => {
            return new Promise((resolve, reject) => {
              const deleteReq = indexedDB.deleteDatabase(db.name);
              deleteReq.onsuccess = () => resolve();
              deleteReq.onerror = () => reject(deleteReq.error);
            });
          })
        );
        console.log('IndexedDB cleared');
      } catch (error) {
        console.warn('Failed to clear IndexedDB:', error);
      }
    }
    
    return true;
  } catch (error) {
    console.error('Failed to clear caches:', error);
    return false;
  }
};

// Unregister all service workers
export const unregisterServiceWorkers = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
      console.log('All service workers unregistered');
      return true;
    }
    return true;
  } catch (error) {
    console.error('Failed to unregister service workers:', error);
    return false;
  }
};

// Force refresh the application (clear cache and reload)
export const forceRefreshApp = async () => {
  try {
    await clearAllCaches();
    await unregisterServiceWorkers();
    
    // Force reload from server (bypass cache)
    window.location.replace(window.location.href + '?t=' + Date.now());
    
    return true;
  } catch (error) {
    console.error('Failed to force refresh app:', error);
    return false;
  }
};

// Complete logout cleanup
export const performLogoutCleanup = async () => {
  try {
    console.log('Starting logout cleanup...');
    
    // Clear all caches and storage
    await clearAllCaches();
    
    // Unregister service workers
    await unregisterServiceWorkers();
    
    console.log('Logout cleanup completed');
    return true;
  } catch (error) {
    console.error('Logout cleanup failed:', error);
    return false;
  }
};

// Add to home screen instructions for iOS
export const getIOSInstallInstructions = () => {
  return [
    'Tap the Share button at the bottom of the screen',
    'Scroll down and tap "Add to Home Screen"',
    'Tap "Add" to install CalorieNSweat'
  ];
};

// Add to home screen instructions for Android
export const getAndroidInstallInstructions = () => {
  return [
    'Tap the menu (⋮) in the top right corner',
    'Select "Add to Home screen" or "Install app"',
    'Tap "Add" to install CalorieNSweat'
  ];
};
